document.addEventListener('DOMContentLoaded', function() {
  // Global vehicle data, fetched and parsed just once
  let vehicleData = null;
  let vehicleDataPromise = null;

  // Util: Parse a CSV row handling quoted fields
  function parseCSVRow(text) {
    const result = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const char = text.charAt(i);
      if (char === '"') {
        if (inQuotes) {
          if (i + 1 < text.length && text.charAt(i + 1) === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          inQuotes = true;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(field);
        field = '';
      } else {
        field += char;
      }
    }
    result.push(field);
    return result;
  }

  // Util: Clean up quotes around a string
  function cleanQuotes(str) {
    if (!str) return '';
    let cleaned = str.trim();
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
      cleaned = cleaned.substring(1, cleaned.length - 1);
    }
    cleaned = cleaned.replace(/\\"/g, '"').replace(/""/g, '"');
    return cleaned;
  }

  // Util: Parse year ranges "2010-2011+2014"
  function parseYearRanges(yearRangeString) {
    const years = new Set();
    const ranges = yearRangeString.split('+').map(r => r.trim());
    ranges.forEach(range => {
      if (range.includes('-')) {
        const [start, end] = range.split('-').map(y => parseInt(y.trim(), 10));
        if (!isNaN(start) && !isNaN(end)) {
          for (let year = start; year <= end; year++) years.add(year);
        }
      } else if (range) {
        const year = parseInt(range, 10);
        if (!isNaN(year)) years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => a - b);
  }

  // Util: Parse the whole CSV file
  function parseCSVData(csvText) {
    const rows = csvText.split('\n');
    const vehicles = [];
    let currentMake = '';
    for (let i = 0; i < rows.length; i++) {
      if (!rows[i].trim()) continue;
      const row = parseCSVRow(rows[i]);
      if (row.length <= 1 || !row[0].trim()) continue;
      const yearRange = cleanQuotes(row[0]);
      const make = cleanQuotes(row[1]) || currentMake;
      const model = cleanQuotes(row[2]) || '';
      const scoutSize = cleanQuotes(row[3]) || '';
      const productLink = cleanQuotes(row[4]) || '';
      const specificNote = row.length > 5 ? cleanQuotes(row[5]) || '' : '';
      if (make) currentMake = make;
      if (!yearRange || !make || !model || !scoutSize) continue;

      const years = parseYearRanges(yearRange);
      vehicles.push({ make, model, scoutSize, productLink, years, specificNote });
    }
    return vehicles;
  }

  // Fetch data only once for all
  function fetchVehicleDataOnce() {
    if (vehicleData) return Promise.resolve(vehicleData);
    if (!vehicleDataPromise) {
      vehicleDataPromise = fetch('https://docs.google.com/spreadsheets/d/1eKps9QLx-nLmbjtdY3Ylw5tiAOHSAhSrnqvF0WGmWLs/export?format=csv')
        .then(response => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.text();
        })
        .then(data => {
          vehicleData = parseCSVData(data);
          return vehicleData;
        })
        .catch(error => {
          vehicleData = null;
          throw error;
        });
    }
    return vehicleDataPromise;
  }

  function formatYearRanges(years) {
    if (!years || years.length === 0) return '';
    years.sort((a, b) => a - b);
    const ranges = [];
    let rangeStart = years[0], rangeEnd = years[0];
    for (let i = 1; i < years.length; i++) {
      if (years[i] === rangeEnd + 1) {
        rangeEnd = years[i];
      } else {
        if (rangeStart === rangeEnd) {
          ranges.push(`${rangeStart}`);
        } else {
          ranges.push(`${rangeStart}-${rangeEnd}`);
        }
        rangeStart = rangeEnd = years[i];
      }
    }
    if (rangeStart === rangeEnd) {
      ranges.push(`${rangeStart}`);
    } else {
      ranges.push(`${rangeStart}-${rangeEnd}`);
    }
    return ranges.join(', ');
  }

  // ----- Main: For each section -----
  Array.from(document.querySelectorAll('.year-make-model')).forEach(section => (function initSection(section) {
    const yearDropdown = section.querySelector('#year-dropdown');
    const makeDropdown = section.querySelector('#make-dropdown');
    const modelDropdown = section.querySelector('#model-dropdown');
    const searchBtn = section.querySelector('#search-btn');
    const resetBtn = section.querySelector('#reset-btn');
    const resultsContainer = section.querySelector('#results-container');

    // Initialize year dropdown
    const currentYear = new Date().getFullYear();
    yearDropdown.innerHTML = '<option value="">Select Year</option>';
    for (let year = currentYear; year >= 2010; year--) {
      const option = document.createElement('option');
      option.value = year;
      option.textContent = year;
      yearDropdown.appendChild(option);
    }

    // Show loading until data is ready
    resultsContainer.innerHTML = '<div class="no-results">Loading vehicle data...</div>';

    fetchVehicleDataOnce()
      .then(_ => {
        resetForm();
        resultsContainer.innerHTML = '<div class="no-results">Select filters above to find compatible Scout models</div>';
      })
      .catch(error => {
        resultsContainer.innerHTML = '<div class="no-results">Error loading vehicle data. Please try again later.</div>';
      });

    yearDropdown.addEventListener('change', handleYearChange);
    makeDropdown.addEventListener('change', handleMakeChange);
    modelDropdown.addEventListener('change', handleModelChange);
    searchBtn.addEventListener('click', handleSearch);
    resetBtn.addEventListener('click', resetForm);

    function handleYearChange() {
      const selectedYear = parseInt(yearDropdown.value, 10);
      if (!selectedYear || !vehicleData) {
        makeDropdown.innerHTML = '<option value="">Select Make</option>';
        makeDropdown.disabled = true;
        modelDropdown.innerHTML = '<option value="">Select Model</option>';
        modelDropdown.disabled = true;
        enableDisableSearchBtn();
        return;
      }
      const filteredMakes = [];
      vehicleData.forEach(vehicle => {
        if (vehicle.years.includes(selectedYear) && !filteredMakes.includes(vehicle.make))
          filteredMakes.push(vehicle.make);
      });
      filteredMakes.sort();
      makeDropdown.innerHTML = '<option value="">Select Make</option>';
      filteredMakes.forEach(make => {
        const option = document.createElement('option');
        option.value = make;
        option.textContent = make;
        makeDropdown.appendChild(option);
      });
      makeDropdown.disabled = filteredMakes.length === 0;
      modelDropdown.innerHTML = '<option value="">Select Model</option>';
      modelDropdown.disabled = true;
      enableDisableSearchBtn();
    }

    function handleMakeChange() {
      const selectedYear = parseInt(yearDropdown.value, 10);
      const selectedMake = makeDropdown.value;
      if (!selectedYear || !selectedMake || !vehicleData) {
        modelDropdown.innerHTML = '<option value="">Select Model</option>';
        modelDropdown.disabled = true;
        enableDisableSearchBtn();
        return;
      }
      const filteredModels = [];
      vehicleData.forEach(vehicle => {
        if (vehicle.years.includes(selectedYear) && vehicle.make === selectedMake && !filteredModels.includes(vehicle.model)) {
          filteredModels.push(vehicle.model);
        }
      });
      filteredModels.sort();
      modelDropdown.innerHTML = '<option value="">Select Model</option>';
      filteredModels.forEach(model => {
        const option = document.createElement('option');
        option.value = model;
        option.textContent = model;
        modelDropdown.appendChild(option);
      });
      modelDropdown.disabled = filteredModels.length === 0;
      enableDisableSearchBtn();
    }

    function handleModelChange() {
      enableDisableSearchBtn();
    }

    function enableDisableSearchBtn() {
      let modelValue = modelDropdown.value;
      if (modelValue == "") {
        searchBtn.setAttribute("disabled", "disabled");
      } else {
        searchBtn.removeAttribute("disabled");
      }
    }

    function handleSearch() {
      if (!vehicleData) return;
      const selectedYear = parseInt(yearDropdown.value, 10);
      const selectedMake = makeDropdown.value;
      const selectedModel = modelDropdown.value;
      if (!selectedYear) return;
      let results = vehicleData.filter(vehicle => vehicle.years.includes(selectedYear));
      if (selectedMake) results = results.filter(vehicle => vehicle.make === selectedMake);
      if (selectedModel) results = results.filter(vehicle => vehicle.model === selectedModel);
      displayResults(results);
    }

    function getSelectedValues() {
      // ONLY for THIS section's selects, not globally!
      return Array.from(section.querySelectorAll(".filter-column select"))
        .map(sel => sel.value || "")
        .filter(val => val !== "")
        .join("-") || "";
    }

    function displayResults(results) {
      if (results.length === 0) {
        resultsContainer.innerHTML = '<div class="no-results">No compatible Scout models found for your selection.</div>';
        return;
      }
      let html = '';
      results.forEach(vehicle => {
        let vehicleRedirectionBtn = '';
        let scoutSize = '';
        if (vehicle.scoutSize.trim() != "" && !vehicle.scoutSize.toLowerCase().includes('n/a')) {
          scoutSize = `We recommend ${vehicle.scoutSize} for`;
        }
        if (vehicle.productLink.trim() != "") {
          let customTag = getSelectedValues();
          vehicleRedirectionBtn = `<a href="${vehicle.productLink}${vehicle.productLink.includes("?") ? '&custom-tag=' + customTag : '?custom-tag=' + customTag}'#product-atc" class="btn btn--secondary" target="_blank">View Product</a>`;
        }
        html += `
          <div class="result-card">
            <h3>${scoutSize} ${vehicle.make} ${vehicle.model}</h3>
            <div class="result-details">
              <p>${vehicle.specificNote}</p>
            </div>
            ${vehicleRedirectionBtn}
          </div>
        `;
      });
      let commonDisclaimer = `<div>
        <p>
          <i>
            ${ window.disclaimerText }
          </i>
        </p>
      </div>`;
      let cardMain = `<div>
        <div class="results-list">
          ${html}
        </div>
        ${commonDisclaimer}
      </div>`;
      resultsContainer.innerHTML = cardMain;
      if(resultsContainer.closest('#SiteHeader')){
        resultsContainer.closest('.ymm_container').classList.add('active');
      }
    }

    function resetForm() {
      yearDropdown.value = '';
      makeDropdown.innerHTML = '<option value="">Select Make</option>';
      makeDropdown.disabled = true;
      modelDropdown.innerHTML = '<option value="">Select Model</option>';
      modelDropdown.disabled = true;
      resultsContainer.innerHTML = '<div class="no-results">Select filters above to find compatible Scout models</div>';
      enableDisableSearchBtn();

      if(resultsContainer.closest('#SiteHeader')){
        resultsContainer.closest('.ymm_container').classList.remove('active');
      }
      
    }
  })(section)); // IIFE per section
});

//  document.addEventListener('DOMContentLoaded', function() {
  
//   // Elements
//   const yearDropdown = document.querySelector('.year-make-model #year-dropdown');
//   const makeDropdown = document.querySelector('.year-make-model #make-dropdown');
//   const modelDropdown = document.querySelector('.year-make-model #model-dropdown');
//   const searchBtn = document.querySelector('.year-make-model #search-btn');
//   const resetBtn = document.querySelector('.year-make-model #reset-btn');
//   const resultsContainer = document.querySelector('.year-make-model #results-container');
  
//   // Variables to store data
//   let vehicleData = [];
//   let filteredMakes = [];
//   let filteredModels = [];
  
//   // Populate year dropdown (2010 to current year)
//   const currentYear = new Date().getFullYear();
//   for (let year = currentYear; year >= 2010; year--) {
//     const option = document.createElement('option');
//     option.value = year;
//     option.textContent = year;
//     yearDropdown.appendChild(option);
//   }
  
//   // Fetch data from Google Sheets
//   fetchVehicleData();
  
//   // Event Listeners
//   yearDropdown.addEventListener('change', handleYearChange);
//   makeDropdown.addEventListener('change', handleMakeChange);
//   modelDropdown.addEventListener('change', handleModelChange);
//   searchBtn.addEventListener('click', handleSearch);
//   resetBtn.addEventListener('click', resetForm);
  
//   // Functions
//   function fetchVehicleData() {
//     fetch('https://docs.google.com/spreadsheets/d/1eKps9QLx-nLmbjtdY3Ylw5tiAOHSAhSrnqvF0WGmWLs/export?format=csv')
//       .then(response => {
//         if (!response.ok) {
//           throw new Error('Network response was not ok');
//         }
//         return response.text();
//       })
//       .then(data => {
//         vehicleData = parseCSVData(data);
//         resultsContainer.innerHTML = '<div class="no-results">Select filters above to find compatible Scout models</div>';
//       })
//       .catch(error => {
//         console.error('Error fetching data:', error);
//         resultsContainer.innerHTML = '<div class="no-results">Error loading vehicle data. Please try again later.</div>';
//       });
//   }
  
//   function parseCSVData(csvText) {
//     const rows = csvText.split('\n');
//     const vehicles = [];
//     let currentMake = '';
    
//     for (let i = 0; i < rows.length; i++) {
//       // Skip empty rows
//       if (!rows[i].trim()) {
//         continue;
//       }
      
//       // Use the proper CSV parsing function instead of simple split
//       const row = parseCSVRow(rows[i]);
      
//       // Skip empty rows
//       if (row.length <= 1 || !row[0].trim()) {
//         continue;
//       }
      
//       // Extract data and clean quotes
//       const yearRange = cleanQuotes(row[0]);
//       const make = cleanQuotes(row[1]) || currentMake;
//       const model = cleanQuotes(row[2]) || '';
//       const scoutSize = cleanQuotes(row[3]) || '';
//       const productLink = cleanQuotes(row[4]) || '';
//       const specificNote = row.length > 5 ? cleanQuotes(row[5]) || '' : '';
      
//       // Update current make for blank cells
//       if (make) {
//         currentMake = make;
//       }
      
//       // Skip if essential data is missing
//       if (!yearRange || !make || !model || !scoutSize) {
//         continue;
//       }
      
//       // Parse year ranges
//       const years = parseYearRanges(yearRange);
      
//       vehicles.push({
//         make,
//         model,
//         scoutSize,
//         productLink,
//         years,
//         specificNote
//       });
//     }
    
//     return vehicles;
//   }
  
//   // Function to properly parse CSV rows with quoted fields
//   function parseCSVRow(text) {
//     const result = [];
//     let field = '';
//     let inQuotes = false;
    
//     for (let i = 0; i < text.length; i++) {
//       const char = text.charAt(i);
      
//       if (char === '"') {
//         if (inQuotes) {
//           // Check for escaped quote (two double quotes together)
//           if (i + 1 < text.length && text.charAt(i + 1) === '"') {
//             field += '"';
//             i++; // Skip the next quote
//           } else {
//             // End of quoted section
//             inQuotes = false;
//           }
//         } else {
//           // Start of quoted section
//           inQuotes = true;
//         }
//       } else if (char === ',' && !inQuotes) {
//         // End of field
//         result.push(field);
//         field = '';
//       } else {
//         field += char;
//       }
//     }
    
//     // Add the last field
//     result.push(field);
//     return result;
//   }
  
//   // Function to clean quotes from a string
//   function cleanQuotes(str) {
//     if (!str) return '';
    
//     let cleaned = str.trim();
    
//     // Remove surrounding quotes if they exist
//     if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
//       cleaned = cleaned.substring(1, cleaned.length - 1);
//     }
    
//     // Replace escaped quotes and double quotes
//     cleaned = cleaned.replace(/\\"/g, '"').replace(/""/g, '"');
    
//     return cleaned;
//   }
  
//   function parseYearRanges(yearRangeString) {
//     const years = new Set();
    
//     // Split by '+' to handle multiple ranges
//     const ranges = yearRangeString.split('+').map(r => r.trim());
    
//     ranges.forEach(range => {
//       if (range.includes('-')) {
//         // Handle year ranges (e.g., 2010-2013)
//         const [start, end] = range.split('-').map(y => parseInt(y.trim(), 10));
//         if (!isNaN(start) && !isNaN(end)) {
//           for (let year = start; year <= end; year++) {
//             years.add(year);
//           }
//         }
//       } else if (range) {
//         // Handle single years
//         const year = parseInt(range.trim(), 10);
//         if (!isNaN(year)) {
//           years.add(year);
//         }
//       }
//     });
    
//     return Array.from(years).sort((a, b) => a - b);
//   }
  
//   function handleYearChange() {
//     const selectedYear = parseInt(yearDropdown.value, 10);
    
//     if (!selectedYear) {
//       // Reset dropdowns if no year selected
//       makeDropdown.innerHTML = '<option value="">Select Make</option>';
//       makeDropdown.disabled = true;
//       modelDropdown.innerHTML = '<option value="">Select Model</option>';
//       modelDropdown.disabled = true;
//       return;
//     }
    
//     // Filter makes based on selected year
//     filteredMakes = [];
//     vehicleData.forEach(vehicle => {
//       if (vehicle.years.includes(selectedYear) && !filteredMakes.includes(vehicle.make)) {
//         filteredMakes.push(vehicle.make);
//       }
//     });
    
//     // Sort makes alphabetically
//     filteredMakes.sort();
    
//     // Update make dropdown
//     makeDropdown.innerHTML = '<option value="">Select Make</option>';
//     filteredMakes.forEach(make => {
//       const option = document.createElement('option');
//       option.value = make;
//       option.textContent = make;
//       makeDropdown.appendChild(option);
//     });
    
//     // Enable make dropdown if options are available
//     makeDropdown.disabled = filteredMakes.length === 0;
    
//     // Reset model dropdown
//     modelDropdown.innerHTML = '<option value="">Select Model</option>';
//     modelDropdown.disabled = true;
//     enableDisableSearchBtn();
//   }
  
//   function handleMakeChange() {
//     const selectedYear = parseInt(yearDropdown.value, 10);
//     const selectedMake = makeDropdown.value;
    
//     if (!selectedYear || !selectedMake) {
//       // Reset model dropdown if no year or make selected
//       modelDropdown.innerHTML = '<option value="">Select Model</option>';
//       modelDropdown.disabled = true;
//       return;
//     }
    
//     // Filter models based on selected year and make
//     filteredModels = [];
//     vehicleData.forEach(vehicle => {
//       if (vehicle.years.includes(selectedYear) && 
//           vehicle.make === selectedMake && 
//           !filteredModels.includes(vehicle.model)) {
//         filteredModels.push(vehicle.model);
//       }
//     });
    
//     // Sort models alphabetically
//     filteredModels.sort();
    
//     // Update model dropdown
//     modelDropdown.innerHTML = '<option value="">Select Model</option>';
//     filteredModels.forEach(model => {
//       const option = document.createElement('option');
//       option.value = model;
//       option.textContent = model;
//       modelDropdown.appendChild(option);
//     });
    
//     // Enable model dropdown if options are available
//     modelDropdown.disabled = filteredModels.length === 0;
//     enableDisableSearchBtn();
//   }

//   function handleModelChange() {
//     enableDisableSearchBtn();
//   }

//   function enableDisableSearchBtn() {
//     let modelValue = modelDropdown.value;
//     if (modelValue == "") {
//       searchBtn.setAttribute("disabled", "disabled");
//     } else {
//       searchBtn.removeAttribute("disabled");
//     }
//   }
  
//   function handleSearch() {
//     const selectedYear = parseInt(yearDropdown.value, 10);
//     const selectedMake = makeDropdown.value;
//     const selectedModel = modelDropdown.value;
    
//     // Validate selections
//     if (!selectedYear) {
//       console.log('Please select a year');
//       return;
//     }
    
//     // Filter results
//     let results = vehicleData.filter(vehicle => 
//       vehicle.years.includes(selectedYear)
//     );
    
//     if (selectedMake) {
//       results = results.filter(vehicle => vehicle.make === selectedMake);
//     }
    
//     if (selectedModel) {
//       results = results.filter(vehicle => vehicle.model === selectedModel);
//     }
    
//     // Display results
//     displayResults(results);
//   }

//   function getSelectedValues() {
//     return Array.from(document.querySelectorAll(".ymm_container .filter-column"))
//       .map(el => el.querySelector("select")?.value || "")
//       .filter(val => val !== "")
//       .join("-") || "";
//   }
  
//   function displayResults(results) {
//     if (results.length === 0) {
//       resultsContainer.innerHTML = '<div class="no-results">No compatible Scout models found for your selection.</div>';
//       return;
//     }
    
//     let html = '';
//     results.forEach(vehicle => {
//       let vehicleRedirectionBtn = '';
//       let scoutSize = '';

//       if (vehicle.scoutSize.trim() != "" && !vehicle.scoutSize.toLowerCase().includes('n/a')) {
//         scoutSize = `Hey, we recommend ${vehicle.scoutSize} for`;
//       }

//       if (vehicle.productLink.trim() != "") {
//         let customTag = getSelectedValues();
//         vehicleRedirectionBtn = `<a href="${vehicle.productLink}${vehicle.productLink.includes("?") ? '&custom-tag=' + customTag : '?custom-tag=' + customTag}" class="btn btn--secondary" target="_blank">View Product</a>`;
//       }
      
//       html += `
//         <div class="result-card">
//           <h3>${scoutSize} ${vehicle.make} ${vehicle.model}</h3>
//           <div class="result-details">
//             <p>${vehicle.specificNote}</p>
//           </div>
//           ${vehicleRedirectionBtn}
//         </div>
//       `;
//     });

//     let commonDiscalimer = `<div>
//         <p>
//           <i>
//             &ldquo; This is our recommendation based on the most popular vehicle fit and best overall user experience.
//             Some popular vehicles are able to utilize more variations than listed.
//             However, if you have rear facing exhaust, we will always recommend a Scout with a 4" or 7" riser.
//             If you have any questions, or if your vehicle is not listed, or you have any questions, please send us a note &rdquo;
//           </i>
//         </p>
//       </div>`

//     let cardMain = `<div>
//       <div class="results-list">
//         ${html}
//       </div>
//       ${ commonDiscalimer }
//     </div>`;
    
//     resultsContainer.innerHTML = cardMain;
//   }
  
//   function formatYearRanges(years) {
//     if (!years || years.length === 0) return '';
    
//     // Sort years
//     years.sort((a, b) => a - b);
    
//     const ranges = [];
//     let rangeStart = years[0];
//     let rangeEnd = years[0];
    
//     for (let i = 1; i < years.length; i++) {
//       if (years[i] === rangeEnd + 1) {
//         // Continue the current range
//         rangeEnd = years[i];
//       } else {
//         // End the current range and start a new one
//         if (rangeStart === rangeEnd) {
//           ranges.push(`${rangeStart}`);
//         } else {
//           ranges.push(`${rangeStart}-${rangeEnd}`);
//         }
//         rangeStart = rangeEnd = years[i];
//       }
//     }
    
//     // Add the last range
//     if (rangeStart === rangeEnd) {
//       ranges.push(`${rangeStart}`);
//     } else {
//       ranges.push(`${rangeStart}-${rangeEnd}`);
//     }
    
//     return ranges.join(', ');
//   }
  
//   function resetForm() {
//     yearDropdown.value = '';
//     makeDropdown.innerHTML = '<option value="">Select Make</option>';
//     makeDropdown.disabled = true;
//     modelDropdown.innerHTML = '<option value="">Select Model</option>';
//     modelDropdown.disabled = true;
//     resultsContainer.innerHTML = '<div class="no-results">Select filters above to find compatible Scout models</div>';
//     enableDisableSearchBtn();
//   }
// });


// // Create a class for the element
// class yearMakeModel extends HTMLElement {
//   constructor() {
//     super();
//     this.showError = false // By default, suppress all console messages
    
//     try {
//       this.collectionPageURL = "/collections/all-products";
//       this.collectionTemplate = "view=year-make-model";

//       this.form = this.querySelector("#year-make-model-form");
//       this.submitBtn = this.querySelector("#reset-btn");
//       this.resetButton = this.querySelector("#reset-btn");
//       this.filterContainerMain = this.querySelector("#filter-container-main");

//       if (!this.form && this.showError) {
//         console.warn("Year-make-model form not found in the DOM");
//       }
//       if (!this.filterContainerMain && this.showError) {
//         console.warn("Filter container main not found in the DOM");
//       }
//     } catch (error) {
//       if (this.showError) {
//         console.error("Error in yearMakeModel constructor:", error);
//       }
//     }
//   }

//   async connectedCallback() {
//     try {
//       this.addEvents();
//       if (location.href.includes(this.collectionPageURL)) {
//         await this.getYmmData(`${location.href.split('?')[0]}?${this.collectionTemplate}`);
        
//         let allTags = location.href.split('?')[0].split("/")[location.href.split('?')[0].split("/").length - 1].split("+");
//         let urlParams_title_includes = new URLSearchParams(window.location.search).get('title_includes');

//         this.disableEmptySelects();

//         let updatedLength = allTags.length;
//         if(this.querySelectorAll(`select[data-send-as="tag"]`).length == allTags.length){
//           updatedLength = updatedLength + 1;
//         }

//         for (let i = 1; i <= updatedLength; i++) {
//           let nextSelectIndex = i + 1;
//           if (nextSelectIndex == updatedLength) {
//             let customUrl = `${this.collectionPageURL}/${allTags.slice(0, (i)).join(" + ")}?${this.collectionTemplate}&select=${nextSelectIndex}&title_includes=${ urlParams_title_includes }`;
//             let nextSelectSelectedValue = allTags[i];
//             if (this.showError) {
//               console.log("Next selected Value::", nextSelectSelectedValue);
//             }
//             this.getYmmDataUpdateSelectOnly(customUrl, nextSelectIndex, nextSelectSelectedValue);
//           }
//         }
//       } else {
//         this.getYmmData(`${this.collectionPageURL}?${this.collectionTemplate}`);
//       }
//     } catch (error) {
//       if (this.showError) {
//         console.error("Error in connectedCallback:", error);
//       }
//       this.filterContainerMain?.classList.remove("active"); // Ensure loading state is removed
//     }
//   }

//   addEvents() {
//     try {
//       let _this = this;
//       const selects = this.querySelectorAll("select");
      
//       if (selects.length === 0 && this.showError) {
//         console.warn("No select elements found to add events to");
//       }
      
//       selects.forEach(el => {
//         if (!el.dataset.index && this.showError) {
//           console.warn("Select element missing data-index attribute:", el);
//           return;
//         }

//         if(!el.classList.contains("event-added")){
//           el.classList.add("event-added");
//           el.addEventListener("change", function(e) {
//             try {
//               const indexParts = e.currentTarget.dataset.index.split("-");
//               if (indexParts.length < 2) {
//                 throw new Error(`Invalid data-index format: ${e.currentTarget.dataset.index}`);
//               }
              
//               let currentSelectIndex = indexParts[1];
//               _this.resetNextSelects(e.currentTarget.dataset.index);
//               _this.updateNextSelect(e.currentTarget.dataset.index);
//             } catch (error) {
//               if (_this.showError) {
//                 console.error("Error in select change handler:", error);
//               }
//             }
//           });
//         }
//       });

//       if (this.form) {
//         if(!this.form.classList.contains("event-added")){
//           this.form.classList.add("event-added")
//           this.form.addEventListener("submit", this.formSubmitHandler.bind(this));
//         }
//       } else if (this.showError) {
//         console.warn("Form element not found, submit handler not attached");
//       }
      
//       if (this.resetButton) {
//         if(!this.resetButton.classList.contains("event-added")){
//           this.resetButton.classList.add("event-added")
//           this.resetButton.addEventListener("click", this.resetFormHandler.bind(this));
//         }
//       } else if (this.showError) {
//         console.warn("Reset button not found, reset handler not attached");
//       }
//     } catch (error) {
//       if (this.showError) {
//         console.error("Error in addEvents:", error);
//       }
//     }
//   }

//   resetNextSelects(selectIndex) {
//     try {
//       if (typeof selectIndex !== "string" || !selectIndex.includes("-")) {
//         if (this.showError) console.error("Invalid selectIndex format. Expected a string in 'prefix-number' format.");
//         return;
//       }
  
//       let selectIndexId = parseInt(selectIndex.split("-")[1], 10);
//       if (isNaN(selectIndexId)) {
//         if (this.showError) console.error("Invalid selectIndex value. Could not extract a number.");
//         return;
//       }
  
//       this.querySelectorAll("select").forEach(el => {
//         try {
//           if (!el.dataset.index || !el.dataset.index.includes("-")) {
//             if (this.showError) console.error("Missing or invalid dataset index on select element.");
//             return;
//           }
  
//           let currSelElIndex = parseInt(el.dataset.index.split("-")[1], 10);
//           if (isNaN(currSelElIndex)) {
//             if (this.showError) console.error("Invalid dataset index. Could not extract a number.");
//             return;
//           }
  
//           if (currSelElIndex > selectIndexId) {
//             el.querySelectorAll("option").forEach(option => {
//               if (option.value !== "") {
//                 option.remove();
//               }
//             });
  
//             // Ensure resetting doesn't break if no empty option exists
//             if (el.querySelector('option[value=""]')) {
//               el.value = "";
//             }
//           }
//         } catch (error) {
//           if (this.showError) console.error("Error processing select element:", error);
//         }
//       });
//     } catch (error) {
//       if (this.showError) console.error("Error in resetNextSelects:", error);
//     }
//   }

  
//   formSubmitHandler(e) {
//     try {
//       e.preventDefault();
//       const result = this.getSelectValuesData(true);
//       if (this.showError) {
//         console.log(result);
//       }
//     } catch (error) {
//       if (this.showError) {
//         console.error("Error in formSubmitHandler:", error);
//       }
//     }
//   }

//   resetFormHandler(e) {
//     try {
//       this.getYmmData(`${this.collectionPageURL}?${this.collectionTemplate}`);
//     } catch (error) {
//       if (this.showError) {
//         console.error("Error in resetFormHandler:", error);
//       }
//     }
//   }

//   getSelectValuesData(redirect = false) {
//     try {
//       let urlTagsParams = [];
//       let urlParams = [];
//       const selects = this.querySelectorAll("select");
      
//       if (selects.length === 0 && this.showError) {
//         console.warn("No select elements found for building URL parameters");
//       }
      
//       selects.forEach(el => {
//         if(el.dataset.sendAs=="tag"){
//          if (el.value.trim() !== "") {
//             urlTagsParams.push(el.value);
//           }
//         }
//         else{
//           if (el.value.trim() !== "") {
//             urlParams.push(el.value);
//           }
//         }
//       });
      
//       if (urlTagsParams.length === 0 && this.showError) {
//         console.warn("No values selected in any dropdown");
//       }
  
//       if (!redirect) {
//         return this.getYmmData(`${this.collectionPageURL}/${urlTagsParams.join("+")}?${ this.collectionTemplate }&title_includes=${ urlParams.join(",") }`);
//       } else {
//         const redirectUrl = encodeURI(`${this.collectionPageURL}/${urlTagsParams.join("+")}?title_includes=${ urlParams.join(",")}`);
//         if (this.showError) {
//           console.log("Redirecting to:", redirectUrl);
//         }
//         location.href = redirectUrl;
//         return redirectUrl;
//       }
//     } catch (error) {
//       if (this.showError) {
//         console.error("Error in getSelectValuesData:", error);
//       }
//       return null;
//     }
//   }

//   updateNextSelect(selectIndex) {
//     try {
//       let urlTagsParams = [];
//       let urlParams = [];
//       let selectIndexId = parseInt(selectIndex.split("-")[1]);
      
//       if (isNaN(selectIndexId)) {
//         throw new Error(`Invalid select index: ${selectIndex}`);
//       }
      
//       let nextIndex = `${selectIndexId + 1}`;
      
//       for (let i = 0; i <= selectIndexId; i++) {
//         let selectEl = document.querySelector(`#year-make-model-form [data-index="select-${i}"]`);
        
//         if (selectEl) {

//           if (this.showError) {
//             console.log(selectEl.value);
//           }

//           if(selectEl.dataset.sendAs=="tag"){
//             urlTagsParams.push(selectEl.value);  
//           }
//           else{
//             urlParams.push(selectEl.value);
//           }
          
          
//         } else if (this.showError) {
//           console.warn(`Dropdown with data-index="select-${i}" not found.`);
//         }
//       }
      
//       if (urlTagsParams.length === 0 && this.showError) {
//         console.warn("No values collected from select elements");
//         return;
//       }
      
      
//       this.getYmmDataUpdateSelectOnly(`${this.collectionPageURL}/${urlTagsParams.join("+")}?${this.collectionTemplate}&select=${nextIndex}${ urlParams.length > 0 ? '&title_includes=' + urlParams.join(","): '' }`, nextIndex);
//     } catch (error) {
//       if (this.showError) {
//         console.error("Error in updateNextSelect:", error);
//       }
//     }
//   }

//   disableEmptySelects() {

//     try {
//       // Select all <select> elements
//       this.querySelectorAll('select').forEach((el, index) => {

//         // Check if the element has options
//         if (el.options.length === 1 && el.options[0].value.trim() === "") {
//           el.disabled = true; // Disable if only empty option exists
//         }
//         else {
//           el.disabled = false; // Optional: Re-enable if it has valid options
//         }

//         if(index > 0){
//           if(this.querySelectorAll('select')[index - 1].disabled){
//             el.disabled = true;
//           }

//           if(!this.querySelectorAll('select')[index - 1].disabled && this.querySelectorAll('select')[index - 1].value.trim() == ""){
//             el.disabled = true;
//           }
//         }
//       });
//     } catch (error) {
//       // Handle any errors that occur during execution
//       if (this.showError) {
//         console.error('An error occurred while disabling empty selects:', error);
//         // Optionally, display an error message to the user
//         alert('An error occurred while processing the selects. Please check the console for details.');
//       }
//     }
//   }
//   getYmmDataUpdateSelectOnly(urlData, nextSelectIndex, nextSelectSelectedValue = null) {    
//     try {
//       if (!urlData) {
//         throw new Error("URL data is required");
//       }
      
//       if (!nextSelectIndex) {
//         throw new Error("Next select index is required");
//       }
      
//       let _this = this;
      
//       if (!_this.filterContainerMain && this.showError) {
//         console.warn("Filter container main element not found");
//         return;
//       }
      
//       _this.filterContainerMain?.classList.add("active");
      
//       return fetch(urlData)
//         .then(response => {
//           if (!response.ok) {
//             throw new Error(`HTTP error! Status: ${response.status}`);
//           }
//           return response.text();
//         })
//         .then(html => {
//           const parser = new DOMParser();
//           const doc = parser.parseFromString(html, 'text/html');

//           if (_this.showError) {
//             console.log(nextSelectIndex);
//           }
          
//           const targetSelect = _this.querySelector(`[data-index="select-${nextSelectIndex}"]`);
//           const sourceSelect = doc.querySelector(`[data-index="select-${nextSelectIndex}"]`);
          
//           if (!targetSelect) {
//             throw new Error(`Target select with data-index="select-${nextSelectIndex}" not found`);
//           }
          
//           if (!sourceSelect) {
//             throw new Error(`Source select with data-index="select-${nextSelectIndex}" not found in fetched HTML`);
//           }
          
//           targetSelect.outerHTML = sourceSelect.outerHTML;

//           if (nextSelectSelectedValue) {
//             const updatedSelect = _this.querySelector(`[data-index="select-${nextSelectIndex}"]`);
//             if (updatedSelect) {
//               updatedSelect.value = nextSelectSelectedValue;
//             } else if (_this.showError) {
//               console.warn(`Updated select with data-index="select-${nextSelectIndex}" not found after DOM update`);
//             }
//           }
          
//           // Re-attach event listeners to the new select element
//           _this.addEvents();
//         })
//         .catch(error => {
//           if (_this.showError) {
//             console.error('Error fetching or updating select element:', error);
//           }
//         })
//         .finally(() => {
//           if (_this.showError) {
//             console.log("getYmmDataUpdateSelectOnly completed");
//           }
//           _this.disableEmptySelects();
//           setTimeout(function(){
//             _this.filterContainerMain?.classList.remove("active");
//           }, 1000);
//         });
//     } catch (error) {
//       if (this.showError) {
//         console.error("Error in getYmmDataUpdateSelectOnly:", error);
//       }
//       this.filterContainerMain?.classList.remove("active");
//     }
//   }
  
//   async getYmmData(urlData) {
//     try {
//       if (!urlData) {
//         throw new Error("URL data is required");
//       }
      
//       let _this = this;
      
//       if (!_this.filterContainerMain && this.showError) {
//         console.warn("Filter container main element not found");
//         return;
//       }
      
//       _this.filterContainerMain?.classList.add("active");
      
//       return fetch(urlData)
//         .then(response => {
//           if (!response.ok) {
//             throw new Error(`HTTP error! Status: ${response.status}`);
//           }
//           return response.text();
//         })
//         .then(html => {
//           const parser = new DOMParser();
//           const doc = parser.parseFromString(html, 'text/html');
//           const newFilterContainer = doc.querySelector('#filter-container');
      
//           if (newFilterContainer) {
//             const currentFilterContainer = document.querySelector('#filter-container');
//             if (currentFilterContainer) {
//               currentFilterContainer.innerHTML = newFilterContainer.innerHTML;
//               _this.addEvents();
//             } else if (_this.showError) {
//               console.error("Error: #filter-container not found in current DOM");
//             }
//           } else if (_this.showError) {
//             console.error("Error: #filter-container not found in fetched HTML");
//           }
//         })
//         .catch(error => {
//           if (_this.showError) {
//             console.error('Error fetching the filter container:', error);
//           }
//         })
//         .finally(() => {
//           if (_this.showError) {
//             console.log("getYmmData completed");
//           }
//           _this.disableEmptySelects();
//           _this.filterContainerMain?.classList.remove("active");
//         });
//     } catch (error) {
//       if (this.showError) {
//         console.error("Error in getYmmData:", error);
//       }
//       this.filterContainerMain?.classList.remove("active");
//     }
//   }
// }

// // Check if the custom element is already defined before defining it
// if (!customElements.get("year-make-model")) {
//   customElements.define("year-make-model", yearMakeModel);
// } else if (window.console && window.console.warn) {
//   // Only show this warning if the browser console exists and showError is true
//   // This is outside the class, so we need to check console existence explicitly
//   console.warn("Custom element 'year-make-model' is already defined");
// }